// created by AI

import type { AppStructure, AppStructurePayload, AssetObject, CustomerData, Sensor } from "api"

/**
 * Expands the normalized API payload into the legacy object graph consumed throughout the portal.
 *
 * Keeping this conversion at the network boundary avoids repeating shared sensor metadata over the
 * wire without requiring every existing component to resolve definition IDs itself.
 *
 * @param payload - Normalized company tree and its shared sensor metadata dictionaries.
 * @returns The existing app-structure shape with complete sensor and sensor-group objects.
 */
export function expandAppStructurePayload(payload: AppStructurePayload): AppStructure {
	const expandCustomer = (customer: AppStructurePayload["structure"][string]): CustomerData => {
		const clients = Object.fromEntries(
			Object.entries(customer.clients).map(([name, client]) => [name, expandCustomer(client)])
		)
		const plants = Object.fromEntries(
			Object.entries(customer.plants).map(([name, plant]) => {
				const assets: AssetObject[] = plant.assets.map((asset) => {
					const sensors: Sensor[] = []
					const sensorGroupsById = new Map<number, AssetObject["sensorGroups"][number]>()

					for (const sensorReference of asset.sensors) {
						const definition = payload.sensorDefinitions[sensorReference.definitionId]
						if (definition == undefined) {
							throw new Error(
								`App structure references missing sensor definition ${sensorReference.definitionId}`
							)
						}
						const unit =
							definition.unitId == undefined
								? undefined
								: payload.units[definition.unitId]
						if (definition.unitId != undefined && unit == undefined) {
							throw new Error(
								`App structure references missing unit ${definition.unitId}`
							)
						}
						sensors.push({
							display_name: definition.displayName,
							id: sensorReference.id,
							isVirtual: definition.isVirtual,
							republish_list: [],
							unit: unit ?? "",
							unitId: definition.unitId,
						})

						for (const groupId of definition.groupIds) {
							const sharedGroup = payload.sensorGroups[groupId]
							if (sharedGroup == undefined) {
								throw new Error(
									`App structure references missing sensor group ${groupId}`
								)
							}
							let sensorGroup = sensorGroupsById.get(groupId)
							if (sensorGroup == undefined) {
								sensorGroup = { id: groupId, name: sharedGroup.name, sensorIds: [] }
								sensorGroupsById.set(groupId, sensorGroup)
							}
							sensorGroup.sensorIds.push(sensorReference.id)
						}
					}

					return {
						display_name: asset.display_name,
						id: asset.id,
						sensorGroups: Array.from(sensorGroupsById.values()),
						sensors,
					}
				})
				return [name, { ...plant, assets }]
			})
		)

		return { ...customer, clients, plants }
	}

	return Object.fromEntries(
		Object.entries(payload.structure).map(([name, customer]) => [
			name,
			expandCustomer(customer),
		])
	)
}
